import { getPlatform } from '@tbitop/platform';
import type { KeycloakInstance } from 'keycloak-js';
import { log } from '../log';
import { getUserIdToken } from '../organizations';
import type { Session } from '../session';
import { SessionImpl } from '../session';
import { createTokenStorage, updateTokenStorage } from '../tokens';
import type { AuthenticationConfig } from './authentication-config';
import { ApplicationNotEnabledError } from './errors';
import { createKeycloakInstance } from './keycloak-instance';
import { hasAccessToResource } from './openid-configuration';
import type { PlatformAuthenticationConfig } from './platform-authentication-config';
import { toPlatformAuthenticationConfig } from './platform-authentication-config';

const identitiesRealm = 'oneplatform';

export let currentSession: Session | null = null;

export async function authenticate(authenticationConfig: AuthenticationConfig): Promise<Session | null> {
  const config = toPlatformAuthenticationConfig(authenticationConfig, getPlatform());

  let realm = getRealmFromCurrentUrl();

  // if the realm from the current URL is not an organisation realm this means
  // that either the user authentication has not happened yet or that this is the
  // callback of the user authentication
  if (!realm) {
    const usersInstance = await handleUserAuthenticationCallback(config);

    realm = await handleUserRealmSelection(usersInstance.idTokenParsed);
  }

  const keycloak = await handleOrganisationAuthenticationCallback(config, realm);

  currentSession = new SessionImpl(keycloak);
  return currentSession;
}

async function handleUserRealmSelection(idToken: KeycloakInstance['idTokenParsed']) {
  const { preferred_username: username, activeOrganization } = getUserIdToken(idToken);

  log.debug(`The currently selected organisation for ${username} is ${activeOrganization?.name}.`);

  return activeOrganization.name;
}

async function handleUserAuthenticationCallback(config: PlatformAuthenticationConfig) {
  return handleAuthenticationCallback(config, identitiesRealm);
}

async function handleOrganisationAuthenticationCallback(config: PlatformAuthenticationConfig, realm: string) {
  if (!(await hasAccessToResource(config, realm))) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    throw new ApplicationNotEnabledError(realm, config.resourceName!);
  }

  const keycloakInstance = await handleAuthenticationCallback(config, identitiesRealm);

  return keycloakInstance;
}

async function handleAuthenticationCallback(config: PlatformAuthenticationConfig, realm: string) {
  const url = config.getInstanceUrl();
  const tokenStorage = createTokenStorage(url, realm, config.clientId);
  const tokens = await tokenStorage.get();

  const instance = await createKeycloakInstance(config, realm, tokens);
  const currentUrl = new URL(window.location.href);

  // if the user could not be authenticated via silent SSO, we need to trigger
  // the login explicitly
  if (!instance.authenticated) {
    log.debug(`Triggering login for realm ${realm}.`);
    // in a web app, the login will redirect to the login page and prevent the
    // rest of this function to be executed
    // in a mobile app, the login will open an in app browser and the keycloak
    // adapter will wait for the in app browser to close
    await instance.login({
      // we pass the realm as URI param in order to determine that the callback
      // happens as part of the organisation authentication
      redirectUri: redirectUriForRealm(config.redirectUri, realm),
      idpHint: currentUrl.searchParams.has('idp') ? (currentUrl.searchParams.get('idp') || undefined) : undefined,

    });
  }

  await updateTokenStorage(tokenStorage, instance);

  // remove the realm parameter from the current URL as we do not need it anymore
  removeRealmFromCurrentUrl();

  return instance;
}

function getRealmFromCurrentUrl() {
  const currentUrl = new URL(window.location.href);
  return currentUrl.searchParams.get('realm');
}

function removeRealmFromCurrentUrl() {
  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.has('realm')) {
    currentUrl.searchParams.delete('realm');
    window.history.replaceState({}, document.title, currentUrl.toString());
  }
}

function redirectUriForRealm(redirectUri: string, realm: string) {
  const url = new URL(redirectUri);
  url.searchParams.set('realm', realm);
  return url.toString();
}
